/**
 * Renaming, retiring and deleting a channel.
 *
 * Everything here is applied as a draft the user can walk away from, because a
 * select that was tabbed past is not a decision anybody made. Deletion is the
 * one thing on this panel that cannot be undone, so it asks for the channel's
 * name to be typed out — the same gesture every tool with a destructive button
 * has settled on, for the same reason.
 */

import { useRef, useState } from 'react'
import type { ChangeEvent, ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'

import { isApiError } from '@/api/client'
import { useDeleteRoom, useUpdateRoom } from '@/api/queries'
import type { Room, RoomVisibility, UpdateRoomRequest } from '@/api/types'
import { Button, Modal, Select, TextArea, TextField, useToast } from '@/components/ui'
import { formatDay } from '@/lib/format'
import panel from './DetailsPanel.module.css'
import styles from './RoomSettings.module.css'

const VISIBILITY_OPTIONS = [
  { value: 'private', label: 'Private — invite only' },
  { value: 'public', label: 'Public — anyone can join' },
]

interface Draft {
  name: string
  topic: string
  visibility: RoomVisibility
}

function draftOf(room: Room): Draft {
  return {
    name: room.name ?? '',
    topic: room.topic ?? '',
    visibility: room.visibility,
  }
}

/**
 * Trimmed, and joined on a separator a name cannot contain, so that Save
 * lights up for exactly the changes `save` would actually send.
 */
function signature(draft: Draft): string {
  return [draft.name.trim(), draft.topic.trim(), draft.visibility].join('\u0000')
}

function partnerList(room: Room): string {
  const partners = room.shared_with ?? []
  return partners.length === 0 ? 'another server' : partners.join(', ')
}

function describe(error: unknown, fallback: string): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return fallback
}

export interface RoomSettingsProps {
  room: Room
  canDelete: boolean
}

export function RoomSettings({ room, canDelete }: RoomSettingsProps): ReactElement {
  const navigate = useNavigate()
  const toast = useToast()
  const updateRoom = useUpdateRoom()
  const deleteRoom = useDeleteRoom()

  const stored = draftOf(room)
  const storedSignature = signature(stored)

  const [draft, setDraft] = useState<Draft>(stored)
  const [nameError, setNameError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [confirmation, setConfirmation] = useState('')

  // Follows the room as events arrive, without discarding an edit in progress
  // when a refetch returns the very same values.
  const lastStored = useRef(storedSignature)
  if (lastStored.current !== storedSignature) {
    lastStored.current = storedSignature
    setDraft(stored)
    setNameError(null)
  }

  const dirty = signature(draft) !== storedSignature
  const archived = room.archived_at !== null
  const confirmTarget = room.name ?? room.slug ?? 'delete'

  const save = (): void => {
    const name = draft.name.trim()
    if (name === '') {
      setNameError('A channel needs a name.')
      return
    }
    setNameError(null)

    const changes: UpdateRoomRequest = {}
    if (name !== stored.name) changes.name = name
    if (draft.topic.trim() !== stored.topic) changes.topic = draft.topic.trim() === '' ? null : draft.topic.trim()
    if (draft.visibility !== stored.visibility) changes.visibility = draft.visibility

    if (Object.keys(changes).length === 0) return

    updateRoom.mutate(
      { roomId: room.id, changes },
      {
        onSuccess: () => toast.success('The channel was updated.'),
        onError: (error) => toast.error(describe(error, 'The changes could not be saved.')),
      },
    )
  }

  const setArchived = (next: boolean): void => {
    updateRoom.mutate(
      { roomId: room.id, changes: { archived_at: next ? new Date().toISOString() : null } },
      {
        onSuccess: () =>
          toast.success(next ? 'The channel was archived.' : 'The channel was restored.'),
        onError: (error) =>
          toast.error(
            describe(error, next ? 'The channel could not be archived.' : 'The channel could not be restored.'),
          ),
      },
    )
  }

  const confirmDeletion = (): void => {
    if (confirmation.trim() !== confirmTarget) return

    deleteRoom.mutate(room.id, {
      onSuccess: () => {
        setConfirming(false)
        toast.success(`${confirmTarget} was deleted.`)
        navigate('/')
      },
      onError: (error) => toast.error(describe(error, 'The channel could not be deleted.')),
    })
  }

  return (
    <div className={styles.settings}>
      <div className={styles.form}>
        <TextField
          className={styles.compactField}
          label="Name"
          labelExtra={`${draft.name.length}/80`}
          value={draft.name}
          error={nameError}
          maxLength={80}
          hint={room.slug === null ? undefined : `#${room.slug}`}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const name = event.target.value
            setDraft((current) => ({ ...current, name }))
            if (nameError !== null) setNameError(null)
          }}
        />

        <TextArea
          className={`${styles.compactField} ${styles.topicField}`}
          label="Topic"
          labelExtra={`${draft.topic.length}/280`}
          rows={2}
          maxLength={280}
          value={draft.topic}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
            const topic = event.target.value
            setDraft((current) => ({ ...current, topic }))
          }}
        />

        <Select
          className={styles.compactField}
          label="Visibility"
          value={draft.visibility}
          options={VISIBILITY_OPTIONS}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => {
            const value = event.target.value
            if (value !== 'public' && value !== 'private') return
            setDraft((current) => ({ ...current, visibility: value }))
          }}
        />

        <div className={styles.saveBar}>
          {dirty ? (
            <Button size="sm" variant="ghost" onClick={() => setDraft(stored)}>
              Discard
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="primary"
            disabled={!dirty}
            loading={updateRoom.isPending}
            onClick={save}
          >
            Save
          </Button>
        </div>
      </div>

      <div className={styles.secondaryActions}>
        <section className={styles.actionRow}>
          <div className={styles.actionCopy}>
            <h3 className={styles.actionTitle}>
              {archived ? 'Restore channel' : 'Archive channel'}
            </h3>
            <p className={styles.actionHint}>
              {room.archived_at !== null
                ? `Archived ${formatDay(room.archived_at)}.`
                : 'Keeps the history, but stops new messages.'}
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            loading={updateRoom.isPending}
            onClick={() => setArchived(!archived)}
          >
            {archived ? 'Restore' : 'Archive'}
          </Button>
        </section>

        {canDelete ? (
          <section className={styles.actionRow}>
            <div className={styles.actionCopy}>
              <h3 className={`${styles.actionTitle} ${styles.dangerTitle}`}>Delete channel</h3>
              <p className={styles.actionHint}>Removes all messages and files.</p>
            </div>
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                setConfirmation('')
                setConfirming(true)
              }}
            >
              Delete
            </Button>
          </section>
        ) : null}
      </div>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Delete channel"
        size="sm"
        closeOnOverlayClick={false}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={confirmation.trim() !== confirmTarget}
              loading={deleteRoom.isPending}
              onClick={confirmDeletion}
            >
              Delete for everyone
            </Button>
          </>
        }
      >
        <div className={panel.confirmText}>
          <p>
            {`Deleting ${confirmTarget} permanently removes the channel, its messages and files.`}
          </p>
          {room.is_shared ? (
            <p>
              {`Copies already delivered to ${partnerList(room)} remain on those servers.`}
            </p>
          ) : null}
          <TextField
            label={`Type ${confirmTarget} to confirm`}
            autoComplete="off"
            value={confirmation}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setConfirmation(event.target.value)}
          />
        </div>
      </Modal>
    </div>
  )
}

export default RoomSettings
